import styles from './GradedLayerRow.module.css';

/*
    GRADED LAYER ROW (soil, land capability)
        Label column, then one line per class: its name, its share, and a bar
        beneath drawing that share. Davin's pick, 21/08 (treatment 3).

        Four passes on BBB-454, and the reasoning matters because two of them
        reverse each other. The first removed the accordion and the repetition.
        The second removed the hollow middle it left, by putting the rows on a
        label column plus a content column. The third put the shares in pills,
        because on land capability the class is itself a number and "3.1 78%" was
        two numbers touching. The fourth, this one, replaced the pills with bars.

        The bar was deleted in pass two for drawing a number already on screen,
        and it is deliberately back: with all five rows of the panel on screen the
        pills competed with the state chips on the NVZ, protected-site and flood
        rows, two meanings on one shape, and the bar is not a chip. It is the only
        treatment where the make-up reads without reading numbers. The number
        stays beside it, which is the trade accepted when picking this.

        Every fact appears exactly once: the dominant class is not repeated, and a
        lone class filling the whole field draws no bar and prints no percentage,
        because "100%" and a full bar both say what one class already says.
        props:
            title: str, farmer-friendly layer title
            result: layer result object from getZoneLandData (status 'ok').
                Classes may carry a `description`, the few words that layer's own
                provider publishes for that level.
            source: str, provenance (e.g. "Soilscapes at 1:250,000")
            learnMore: { url: str, label: str } | undefined
*/

// All user-facing copy for this component lives here (copy review).
// "Zone" rather than "field": it is the app's own word for the thing a farmer
// draws, and a zone is not always a field (Davin, 11/09/2026).
const STRINGS = {
  noClasses: "No map information found for this zone.",
  basedOn: (source) => `Based on ${source}`,
  partialRead: "Large zone, so this reads part of it only.",
};

function formatPct(fraction) {
  const value = (typeof fraction === 'number' ? fraction : 0) * 100;
  if (value > 0 && value < 1) {
    return "<1%";
  }
  return `${Math.round(value)}%`;
}

function classLabel(landClass) {
  const name = landClass.name != null ? String(landClass.name) : "";
  const code = landClass.code != null ? String(landClass.code) : "";
  if (name && code && name !== code) {
    return `${name} (${code})`;
  }
  return name || code;
}

// 0 to 100, clamped, for the bar width. A provider can return a fraction a
// rounding hair over 1, and a bar wider than its track is a visible bug.
// Exported so the clamp can be asserted without reaching into the DOM for a
// decorative element.
export function barWidth(fraction) {
  const value = (typeof fraction === 'number' ? fraction : 0) * 100;
  return Math.min(100, Math.max(0, value));
}

const GradedLayerRow = (props) => {
  const { result } = props;

  const classes = Array.isArray(result.classes) ? result.classes : [];
  const dominant = classes.length > 0 ? classes[0] : null;

  if (!dominant) {
    return (
      <div className={styles.card}>
        <span className={styles.label}>{props.title}</span>
        <div className={styles.content}>
          <span className={styles.quiet}>{STRINGS.noClasses}</span>
        </div>
      </div>
    );
  }

  // Gate on the *displayed* percentage, not the raw fraction, so a value that
  // rounds up to 100% counts as the whole field.
  const isWholeField = classes.length === 1 && formatPct(dominant.areaFraction) === '100%';
  const hasFooter = Boolean(props.source) || Boolean(props.learnMore);

  return (
    <div className={styles.card}>
      <span className={styles.label}>{props.title}</span>
      <div className={styles.content}>
        {isWholeField ? (
          <p className={styles.soleLine}>
            <span className={styles.name}>{classLabel(dominant)}</span>
            {dominant.description ? (
              <span className={styles.desc}>{dominant.description}</span>
            ) : null}
            {dominant.sentence ? (
              <span className={styles.sentence}>{dominant.sentence}</span>
            ) : null}
          </p>
        ) : (
          <div className={styles.makeup}>
            {classes.map((landClass, index) => (
              <div key={index} className={styles.share}>
                <p className={index === 0 ? styles.lineMain : styles.line}>
                  <span className={styles.name}>{classLabel(landClass)}</span>
                  {landClass.description ? (
                    <span className={styles.desc}>{landClass.description}</span>
                  ) : null}
                  <span className={styles.pct}>{formatPct(landClass.areaFraction)}</span>
                </p>
                {/* Only the dominant class carries a sentence, set upstream in
                    aggregateLandSummary, so no index check is needed here. */}
                {landClass.sentence ? (
                  <p className={styles.sentence}>{landClass.sentence}</p>
                ) : null}
                {/* Decorative: the share is already on the line above as text,
                    so the bar adds nothing for a screen reader and announcing it
                    twice would be worse than hiding it. */}
                <span className={styles.track} aria-hidden="true">
                  <span
                    className={index === 0 ? styles.fill : styles.fillMinor}
                    style={{ width: `${barWidth(landClass.areaFraction)}%` }}
                  />
                </span>
              </div>
            ))}
          </div>
        )}

        {result.truncated ? (
          <p className={styles.caveat}>{STRINGS.partialRead}</p>
        ) : null}

        {hasFooter ? (
          <p className={styles.footer}>
            {props.source ? (
              <span className={styles.source}>{STRINGS.basedOn(props.source)}</span>
            ) : null}
            {props.source && props.learnMore ? (
              <span className={styles.footRule} aria-hidden="true" />
            ) : null}
            {props.learnMore ? (
              <a
                className={styles.learnMoreLink}
                href={props.learnMore.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.learnMore.label}
              </a>
            ) : null}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default GradedLayerRow;
